import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { makeRowColumn, getHeadObj, cenversionTpCode } from 'lib/commonLib';
import { localTargetReport, localTargetReportSub } from 'lib/api/localreport';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const TargetStatistics = () => {
  const { t } = useTranslation();
  const initParams = {
    sort: 'desc',
    order: 'ADVRTS_AMT',
    pltfomTpCode: 'all',
    itlTpCode: 'all',
    sDate: moment().add(-30, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
  };
  const [params, setParams] = useState(initParams);
  const [initialization, setInitialization] = useState(false);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  const customized = {
    CORP_NAME: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CORP_NAME');
        return !rowObj['PLTFOM_TP_CODE']
          ? makeRowColumn(rowObj['CORP_NAME'], bodyItem1.type, bodyItem1.format)
          : cenversionTpCode(rowObj['PLTFOM_TP_CODE']);
      },
    },
    WEIGHT_PCT: {
      makeBody: (rowObj, headsMap) => {
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          rowObj['WEIGHT_PCT']
        );
      },
    },
    SITE_NAME: {
      makeBody: (rowObj, headsMap) => {
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          rowObj['SITE_NAME']
        );
      },
    },
    MEDIA_ID: {
      makeBody: (rowObj, headsMap) => {
        return !rowObj['isChild'] && rowObj['MEDIA_ID'];
      },
    },
    MEDIA_SITE_NO: {
      makeBody: (rowObj, headsMap) => {
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          rowObj['MEDIA_SITE_NO']
        );
      },
    },
    MEDIA_SCRIPT_NO: {
      makeBody: (rowObj, headsMap) => {
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          rowObj['MEDIA_SCRIPT_NO']
        );
      },
    },
    ACCOUNT_TYPE: {
      makeBody: (rowObj, headsMap) => {
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          rowObj['ACCOUNT_TYPE']
        );
      },
    },
    ADVER_CNT: {
      makeHead: (headsMap) => {
        return (
          <InfoTitleArea
            title={getHeadObj(headsMap, 'ADVER_CNT').label}
            tooltipTitle={t('소진 1,000 이상인 광고주 수')}
            iconType="fainfoCircle"
            iconColor="graph"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADVRTS_AMT');
        return (
          rowObj['CORP_NAME'] !== 'total' &&
          !rowObj['isChild'] &&
          makeRowColumn(rowObj['ADVER_CNT'], bodyItem1.type, bodyItem1.format)
        );
      },
    },
    UM_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return t('성향');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'UM_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'UM_PAR_EPRS_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['UM_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['UM_PAR_EPRS_RATE'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
    },
    UM_PAR_EPRS_RATE: { merged: true },
    AD_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return t('베이스광고');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'AD_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'AD_PAR_EPRS_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['AD_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['AD_PAR_EPRS_RATE'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
    },
    AD_PAR_EPRS_RATE: { merged: true },
    CLICK_RATE: {
      makeHead: (headsMap) => {
        return t('구좌클릭율');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_RATE');
        return makeRowColumn(
          rowObj['CLICK_RATE'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
  };

  /**
   * 테이블 클릭 후 세부정보 받아올때 보낼 param setting 해주는 함수
   * @param {json} data 테이블 로우 데이터
   */
  const makeSubParam = (data) => {
    return {
      mediaScriptNo: data.MEDIA_SCRIPT_NO,
    };
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          useExternalFilter={false}
        />
      </NewFilterWrap>
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={localTargetReport}
        dataDetailReqPromise={localTargetReportSub}
        makeSubParam={makeSubParam}
        customized={customized}
        useTotalSubList
        expandTable
      />
    </>
  );
};

export default TargetStatistics;
